import React from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Блок содержимого: имя, необязательная ссылка справа и всё, что под ними.
 *
 * Страница — это последовательность таких блоков. Имя блока — служебный стиль
 * ui-kit (`Divider` с подписью): 12px строчными, вторичной краской, с линией,
 * уходящей вправо, — граница смысловых блоков, а не заголовок страницы. Рамка
 * остаётся только там, где она правда что-то ограничивает: у панели, списка строк и
 * таблицы.
 */
const styles = StyleSheet.create({
  section: {gap: spacing(3), paddingBottom: spacing(5)},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
  },
  text: {flexShrink: 1, minWidth: 0, gap: 1},
  /* Служебный стиль ui-kit (`sectionTitle`): 12px полужирным, вторичной краской. */
  title: {fontSize: 12, lineHeight: 16},
  /* Линия от имени блока до правого края — граница смыслового блока. */
  rule: {flex: 1, height: StyleSheet.hairlineWidth, minWidth: spacing(3)},
  action: {paddingVertical: spacing(1), paddingLeft: spacing(1)},
});

export function SectionHeader({
  title,
  hint,
  actionLabel,
  onAction,
  aside,
  style,
}: {
  title: string;
  /** Тихая строка под именем — что это за блок. */
  hint?: string;
  /** Ссылка справа. Рисуется только вместе с `onAction`. */
  actionLabel?: string;
  onAction?: () => void;
  /** Всё прочее на одной линии с именем — обычно значок «?». */
  aside?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  return (
    <View style={[styles.row, style]}>
      <View style={styles.text}>
        <AppText variant="subtitle" color={p.textSecondary} style={styles.title}>
          {title}
        </AppText>
        {hint ? <AppText variant="caption">{hint}</AppText> : null}
      </View>
      <View style={[styles.rule, {backgroundColor: p.borderSubtle}]} />
      {aside}
      {actionLabel && onAction ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={actionLabel}
          onPress={onAction}
          hitSlop={8}
          style={styles.action}>
          {state => (
            <AppText
              variant="label"
              color={p.primaryInk}
              style={
                state.pressed || (state as {hovered?: boolean}).hovered
                  ? {textDecorationLine: 'underline'}
                  : undefined
              }>
              {actionLabel}
            </AppText>
          )}
        </Pressable>
      ) : null}
    </View>
  );
}

/** Заголовок блока плюс его содержимое, с готовым ритмом между ними. */
export function Section({
  title,
  hint,
  actionLabel,
  onAction,
  aside,
  children,
  style,
  testID,
}: {
  title?: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
  aside?: React.ReactNode;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  return (
    <View testID={testID} style={[styles.section, style]}>
      {title ? (
        <SectionHeader
          title={title}
          hint={hint}
          actionLabel={actionLabel}
          onAction={onAction}
          aside={aside}
        />
      ) : null}
      {children}
    </View>
  );
}
